import { Breadcrumb, Button } from "pepsico-ds";
import { useMemo, useState } from "react";
import ExportOptionsButton from "../../../components/common/exportOptionsButton/ExportOptionsButton";
import ToastNotification from "../../../components/common/toastNotification/ToastNotification";
import { FileHistoryButton } from "../../../components/fileManager";
import { useKeywordModal } from "../../../hooks/useKeywordModal";
import { useKeywords } from "../../../hooks/useKeywords";
import { paths } from "../../../routes/paths";
import KeywordFileUploadModal from "./KeywordFileUploadModal";
import KeywordModal from "./KeywordModal";
import KeywordUploadMethodModal from "./KeywordUploadMethodModal";
import KeywordsTable from "./KeywordsTable";
import { KEYWORD_GROUPS_EXPORT_CONTEXT, createExportParameters } from "./constants";
import "./keywordManagement.scss";

export default function KeywordManagementPage() {
  const {
    keywords,
    categories,
    loading,
    searchTerm,
    searchCategories,
    handlers,
    refetch,
  } = useKeywords();
  const { handleSearch, handleSearchChange, handleSearchCategory } = handlers;

  const [showMethodModal, setShowMethodModal] = useState(false);
  const [showFileUploadModal, setShowFileUploadModal] = useState(false);

  const {
    isOpen,
    mode,
    formData,
    errors,
    submitAttempted,
    isLoading: modalLoading,
    openModal,
    closeModal,
    handleFormChange,
    handleSubmit,
    toast,
    showToast,
    closeToast,
  } = useKeywordModal();

  const exportParameters = useMemo(
    () => createExportParameters(searchTerm),
    [searchTerm]
  );

  const handleSelectManual = () => {
    setShowMethodModal(false);
    openModal("add", {});
  };

  const handleSelectUpload = () => {
    setShowMethodModal(false);
    setShowFileUploadModal(true);
  };

  const handleFileUploadSuccess = () => {
    showToast(
      "success",
      "Import In Progress",
      "Keyword groups import is being processed"
    );
    if (refetch) {
      refetch();
    }
  };

  const handleEdit = (keyword) => {
    openModal("edit", keyword);
  };

  const handleDelete = (id, name) => {
    openModal("delete", { id, name });
  };

  const handleModalSubmit = async () => {
    const result = await handleSubmit(() => {
      if (refetch) {
        refetch();
      }
    });
    return result;
  };

  return (
    <div className="container">
      <div className="wrap">
        <Breadcrumb
          breadcrumbs={[
            {
              label: "Program Settings",
              href: `../../../${paths.system_setting}`,
            },
            {
              label: "Keyword Management",
              href: `../../../${paths.system_setting}/keyword-management`,
            },
          ]}
          activeIndex={1}
        />
        <div className="keyword-management-container">
          <div className="page-header">
            <h3 className="page-title">Keywords Management</h3>
            <div className="page-header-actions">
              <ExportOptionsButton
                context={KEYWORD_GROUPS_EXPORT_CONTEXT}
                exportType="text/csv"
                filename={`keyword-groups-${Date.now()}.csv`}
                parameters={exportParameters}
              />
              <FileHistoryButton
                contentType="text/csv"
                context={KEYWORD_GROUPS_EXPORT_CONTEXT}
                operationType="EXPORT"
                defaultTab="exports"
                variant="secondary"
              />
              <Button
                text="Add Keyword"
                variant="primary"
                onClick={handleSelectManual}
                className="add-keyword-btn"
              />
            </div>
          </div>
          <KeywordsTable
            keywords={keywords}
            categories={categories}
            loading={loading}
            searchTerm={searchTerm}
            searchCategories={searchCategories}
            handleEdit={handleEdit}
            handleSoftDelete={handleDelete}
            handleSearch={handleSearch}
            handleSearchChange={handleSearchChange}
            handleSearchCategory={handleSearchCategory}
          />
        </div>
        <KeywordUploadMethodModal
          isOpen={showMethodModal}
          onClose={() => setShowMethodModal(false)}
          onSelectManual={handleSelectManual}
          onSelectUpload={handleSelectUpload}
        />
        <KeywordFileUploadModal
          isOpen={showFileUploadModal}
          onClose={() => setShowFileUploadModal(false)}
          onSuccess={handleFileUploadSuccess}
        />
        <KeywordModal
          isOpen={isOpen}
          mode={mode}
          formData={formData}
          errors={errors}
          submitAttempted={submitAttempted}
          isLoading={modalLoading}
          onClose={closeModal}
          onSubmit={handleModalSubmit}
          onFormChange={handleFormChange}
        />
        <ToastNotification
          {...toast}
          handleClose={closeToast}
          sx={{ zIndex: 9999 }}
        />
      </div>
    </div>
  );
}
